// =============================================================================
// Semver utilities — used by Remote App Config for minimum-version gating.
// Accepts plain "MAJOR.MINOR.PATCH" strings. No pre-release / build tags.
// =============================================================================

// Rejects leading zeros ("01.02.03") and empty segments. Paired with a
// server-side .max(32) length cap to keep numeric parsing in the safe-integer
// range — otherwise multi-KB digit strings coerce to Infinity in compareSemver
// and all comparisons collapse to 0.
const SEMVER_REGEX = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

export function isValidSemver(value: unknown): boolean {
  return typeof value === 'string' && SEMVER_REGEX.test(value);
}

/**
 * Compare two plain semver strings.
 * Returns -1 if a < b, 0 if a == b, 1 if a > b.
 * Throws if either input is not a valid semver — callers validate first.
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  if (!isValidSemver(a) || !isValidSemver(b)) {
    throw new Error(`Invalid semver input: ${a}, ${b}`);
  }
  const [aMajor, aMinor, aPatch] = a.split('.').map(Number);
  const [bMajor, bMinor, bPatch] = b.split('.').map(Number);
  if (aMajor !== bMajor) return aMajor < bMajor ? -1 : 1;
  if (aMinor !== bMinor) return aMinor < bMinor ? -1 : 1;
  if (aPatch !== bPatch) return aPatch < bPatch ? -1 : 1;
  return 0;
}
